// Which pending NC1 uploader enrollments the owner's unlocked browser may
// approve WITHOUT a click (user decision 2026-10-06).
//
// A monitored device (phone, Windows machine) that is already registered on
// the account was approved once by the owner, on the Devices page or in the
// dashboard's "Add device request" dialog. When such a device adopts the new
// encryption later - the owner enabled it after the device registered, or
// the device was updated to a build that speaks it - it posts an uploader
// enrollment and waits for a second, by-name approval on /link-device. That
// second click added nothing: the device name is server-supplied, so the
// human never had anything to verify (EPOCH-KEYPAIR.md decision 2 accepts
// the relay key-swap for uploaders regardless). It only produced a trickle
// of "Review" prompts, one per device, spread over the hours each phone took
// to check in.
//
// The rule is deliberately narrow. A request is auto-approvable when ALL of:
//   - it is a pending UPLOADER request with a key and a device uuid;
//   - its device uuid is REGISTERED on the account right now (the caller
//     passes the complete, freshly fetched set - never a stale store copy);
//   - its legacy "Add device request" is NOT still pending (the dashboard
//     dialog approves both halves of those together, 2026-08-17 UX split);
//   - the signed device list has NO entry for that uuid at all. A same-key
//     live entry is the stale re-admission case staleUploaderRequests
//     withdraws; a DIFFERENT key for a uuid already on the list (live or
//     tombstoned) is a key change, and that is the one signal worth a human
//     look (finding ⑯, the same line AutoGrant draws for recipients).
// Without a real device list (no `devices` array) that last check cannot be
// made, so nothing is selected: a key change must never slip through because
// a fetch came back empty.
//
// Pure module (no Vue / API imports) so Node can exercise it directly.

const lower = (s) => String(s || '').toLowerCase()

/**
 * @param {Array} requests  pending enrollment requests as the service returns them
 * @param {{registeredUuids: Iterable<string>, pendingLegacyUuids: Iterable<string>, deviceList: {devices?: Array}}} ctx
 * @returns {Array} the subset of `requests` to approve, in their original order
 */
export function selectAutoApprovableUploaders (requests, { registeredUuids, pendingLegacyUuids, deviceList } = {}) {
  const registered = new Set([...(registeredUuids || [])].map(lower).filter(Boolean))
  if (registered.size === 0) return []
  if (!deviceList || !Array.isArray(deviceList.devices)) return []
  const legacyPending = new Set([...(pendingLegacyUuids || [])].map(lower).filter(Boolean))
  const listed = new Set(deviceList.devices.map((d) => lower(d && d.deviceId)).filter(Boolean))
  const out = []
  for (const r of requests || []) {
    if (!r || r.purpose !== 'uploader' || r.state !== 'pending') continue
    if (typeof r.devSignPk !== 'string' || !r.devSignPk) continue
    const uuid = lower(r.deviceUuid)
    if (!uuid) continue
    if (!registered.has(uuid)) continue
    if (legacyPending.has(uuid)) continue
    if (listed.has(uuid)) continue
    out.push(r)
  }
  return out
}
